import {
  AvailableSlots,
  FNOL_ClaimOwnerOrComplexAvailabilityResponse,
  FNOL_RequestForm,
  FNOL_ResourceResponse,
  FNOL_TaskTypes,
  ResultStatus,
} from '@dispatch/shared/types';
import { textColumn } from '@dispatch/web/ui';
import { Typography } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { UseFormGetValues } from 'react-hook-form';

export type FNOLAvailabilitySectionListProps = {
  taskType: FNOL_TaskTypes;
  availableSlots:
    | AvailableSlots[]
    | FNOL_ClaimOwnerOrComplexAvailabilityResponse;
  status: ResultStatus;
  setSelectedSlot: React.Dispatch<React.SetStateAction<number | null>>;
  timeZone?: string;
  getValues: UseFormGetValues<FNOL_RequestForm>;
};

export const FNOLAvailabilitySectionList = ({
  taskType,
  availableSlots,
  status,
  setSelectedSlot,
  timeZone = '',
  getValues,
}: FNOLAvailabilitySectionListProps) => {
  const ref = useGridApiRef();
  const slots = useMemo(() => {
    if (taskType === FNOL_TaskTypes.NON_COMPLEX) {
      return (availableSlots as AvailableSlots[]).map((slot, index) => ({
        ...slot,
        id: index,
        day: formatInTimeZone(
          slot?.projectedWorkStartTime,
          timeZone,
          'MM/dd/yyyy EEEE'
        ),
      }));
    }

    const response =
      availableSlots as FNOL_ClaimOwnerOrComplexAvailabilityResponse;

    if (response.projectedWorkStartTime) {
      return [
        {
          ...response,
          id: 0,
        },
      ];
    }

    return [];
  }, [availableSlots, taskType, timeZone]);

  const columnGroupingModel = useMemo(() => {
    if (taskType === FNOL_TaskTypes.NON_COMPLEX) {
      return ['day'];
    }
    return [];
  }, [taskType]);

  const columns = useMemo(() => {
    if (taskType !== FNOL_TaskTypes.NON_COMPLEX) {
      return [
        {
          field: 'resource',
          headerName: 'Resource Name',
          flex: 1,
          minWidth: 100,
          valueGetter: (params: FNOL_ResourceResponse) => params?.name,
        },
        textColumn({
          field: 'projectedWorkStartTime',
          headerName: 'Start Time',
          valueFormatter: (params: unknown) =>
            params
              ? formatInTimeZone(params as string, timeZone, 'MM/dd/yyyy')
              : '',
        }),
        textColumn({
          field: 'methodOfInspection',
          headerName: 'Method of Inspection',
        }),
      ];
    } else {
      const columns = [
        textColumn({
          field: 'day',
          headerName: 'Day',
          flex: 1,
          minWidth: 250,
        }),
        textColumn({
          field: 'projectedWorkStartTime',
          headerName: 'Start Time',
          valueFormatter: (params: unknown) =>
            params
              ? formatInTimeZone(params as string, timeZone, 'hh:mm aa')
              : '',
        }),
        textColumn({
          field: 'projectedWorkEndTime',
          headerName: 'End Time',
          valueFormatter: (params: unknown) =>
            params
              ? formatInTimeZone(params as string, timeZone, 'hh:mm aa')
              : '',
        }),
      ];

      if (getValues('resourceIds')?.length > 0) {
        columns.unshift(
          textColumn({
            field: 'resourceName',
            headerName: 'Resource Name',
            flex: 1,
            minWidth: 100,
          })
        );
      }

      return columns;
    }
  }, [getValues, taskType, timeZone]);

  const onRowSelect = (rowSelectionModel: GridRowSelectionModel) => {
    const selectableRow = Number(rowSelectionModel[0]);

    if (!isNaN(selectableRow)) {
      setSelectedSlot(selectableRow);
    }
  };

  return (
    <div style={{ height: '60vh', width: '100%' }}>
      <DataGridPremium
        apiRef={ref}
        loading={status === 'loading'}
        getRowId={(row) => row.id}
        disableColumnFilter
        disableDensitySelector
        rows={slots || []}
        columns={columns as GridColDef[]}
        columnBufferPx={100}
        hideFooter
        hideFooterSelectedRowCount
        initialState={{
          rowGrouping: {
            model: columnGroupingModel,
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Typography variant="h5" color="primary">
              No Availability
            </Typography>
          ),
          noResultsOverlay: () => (
            <Typography variant="h5" color="primary">
              No Availability
            </Typography>
          ),
        }}
        onRowSelectionModelChange={onRowSelect}
      />
    </div>
  );
};
